/**
 * Ends — at the end of the day (when its drops run out, no hard end), or at
 * a specified time (words: user, 15/10/2026) on the Danish clock, the
 * country's time beside it; "+1 day" (past the Danish midnight) only after
 * a Specified start — past midnight
 * is "+1 day". The line under the choice says where the day really ends and
 * how many drops a too-early end cuts.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { localEndToDk } from '@trafficpulse/shared';
import { Flag, X } from 'lucide-react';

import { TimeIntervalPicker } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { DropGenPlanDetail } from '../../../../drop-generator.types';
import { hhmmToMinutes, minutesToHHMM } from '../../../../utils/format';
import { todayIn } from '../../../../utils/clock/today-clock';
import { useCountryClock } from '../../../shared/time/country-clock-context';
import { CountryEcho } from '../../../shared/time/CountryEcho';
import { ClockPair } from '../../../shared/time/ClockPair';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { StepRow, ValueLine } from '../controls/StepRow';
import { CHOICE_CHIP, CHOICE_OFF, CHOICE_ON, MINIMAL_BOX } from '../controls/field-style';
import { ChoiceChips } from '../controls/ChoiceChips';

const OPTIONS = [
  { value: 'RUN_OUT', label: 'End of the day' },
  { value: 'AT', label: 'Specified time' },
] as const;

interface Props {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  /** The last drop that goes (null = none). */
  lastMin: number | null;
  /** The start and the end on the country's clock (the timing mirror's). */
  startMin: number | null;
  endMin: number | null;
  /** Drops the end time cuts. */
  cut: number;
}

/** Nothing goes: no drop listed yet — or every listed one is outside the window (a hard limit). */
const noLastDrop = (draft: ContainerEditor['draft']) =>
  draft.slots.some((s) => s.enabled) ? 'no drop inside the window' : 'no drops yet — add them in Drop slots';

export const EndRow: FC<Props> = ({ editor, plan, lastMin, startMin, endMin, cut }) => {
  const { draft, setField } = editor;
  const end = draft.endAtMin;
  const tz = plan.recipientTimezone;
  const fixed = draft.startMode === 'FIXED_TIME';
  // A new end starts at the window's close, in Danish time (15/10/2026).
  const { runDate } = useCountryClock();
  const firstEnd = () => localEndToDk(plan.windowEndMin, fixed ? startMin : null, todayIn(tz, runDate));
  const pick = (v: 'RUN_OUT' | 'AT') => setField('endAtMin', v === 'AT' ? (end ?? firstEnd()) : null);
  return (
    <StepRow icon={Flag} label="Ends" hint="Specified time = no drop is ever planned after it — Danish time (🇩🇰), the country's beside it. End of the day = no end time: the container runs until its drops run out.">
      <ChoiceChips options={OPTIONS} value={end === null ? 'RUN_OUT' : 'AT'} onChange={pick} ariaLabel="When it ends" />
      <ValueLine key={end === null ? 'run-out' : 'at'}>
        {end === null ? (
          <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            {lastMin === null ? noLastDrop(draft) : <>last drop <ClockPair min={lastMin} strong /></>}
          </span>
        ) : (
          <EndAt end={end} endMin={endMin} nextDayOk={fixed} cut={cut} onChange={(v) => setField('endAtMin', v)} />
        )}
      </ValueLine>
    </StepRow>
  );
};

interface EndAtProps {
  end: number;
  /** The end on the country's clock. */
  endMin: number | null;
  /** "+1 day" counts from the Danish start — only a Specified start has one. */
  nextDayOk: boolean;
  cut: number;
  onChange: (v: number | null) => void;
}

const EndAt: FC<EndAtProps> = ({ end, endMin, nextDayOk, cut, onChange }) => {
  const nextDay = end > 1439;
  return (
    <>
      <span aria-hidden title="Danish time">🇩🇰</span>
      <TimeIntervalPicker
        boxClassName={MINIMAL_BOX}
        value={minutesToHHMM(end)}
        onChange={(v) => {
          const min = hhmmToMinutes(v);
          if (min !== null) onChange(nextDay ? min + 1440 : min);
        }}
      />
      {nextDayOk || nextDay ? <NextDayChip end={end} onChange={onChange} /> : null}
      <CountryEcho min={endMin} className="text-xs" />
      <span className={cn('text-xs', cut > 0 ? 'font-medium text-warning' : 'text-muted-foreground')}>
        {cut > 0 ? `${cut} ${cut === 1 ? 'drop comes' : 'drops come'} after it — cut` : 'every drop fits before it'}
      </span>
      <button type="button" aria-label="Remove the end time" onClick={() => onChange(null)} className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
        <X className="h-3.5 w-3.5" />
      </button>
    </>
  );
};

/** "+1 day": past the Danish midnight — the next calendar day, same day of drops. */
const NextDayChip: FC<{ end: number; onChange: (v: number) => void }> = ({ end, onChange }) => {
  const nextDay = end > 1439;
  return (
    <button
      type="button"
      aria-pressed={nextDay}
      title="Past midnight in Denmark — the next calendar day, same day of drops"
      onClick={() => onChange(nextDay ? end - 1440 : end + 1440)}
      className={cn(CHOICE_CHIP, 'px-3', nextDay ? CHOICE_ON : CHOICE_OFF)}
    >
      +1 day
    </button>
  );
};
